/**
 * App config — works on both Web (Webpack) and Native (Expo).
 *
 * Web:    Values are injected by Webpack DefinePlugin at compile time.
 * Native: Values come from expo-constants (app.json → extra).
 */
import { Platform } from 'react-native';

/* ── Webpack globals (web only — Metro never defines these) ── */
declare const __APP_API_BASE_URL__: string | undefined;
declare const __APP_WS_BASE_URL__: string | undefined;

const DEFAULT_API_BASE_URL = 'https://hgeospatial.cloud/api';
const DEFAULT_WS_BASE_URL = 'wss://hgeospatial.cloud/ws';

function isAllowedBackendHost(hostname: string): boolean {
  return (
    hostname === 'hgeospatial.cloud' ||
    hostname === 'www.hgeospatial.cloud' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    /^10\./.test(hostname) ||
    /^192\.168\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
  );
}

function normalizeBackendBaseUrl(value: string, fallback: string): string {
  const trimmed = value.trim().replace(/\/+$/, '');

  try {
    const hostname = new URL(trimmed).hostname.toLowerCase();
    if (!isAllowedBackendHost(hostname)) {
      return fallback;
    }
  } catch {
    return fallback;
  }

  return trimmed;
}

function getApiBaseUrl(): string {
  if (Platform.OS === 'web' && typeof __APP_API_BASE_URL__ !== 'undefined') {
    return normalizeBackendBaseUrl(__APP_API_BASE_URL__, DEFAULT_API_BASE_URL);
  }
  // Native: read from expo-constants
  try {
    const Constants = require('expo-constants').default;
    let base = Constants.expoConfig?.extra?.apiBaseUrl || DEFAULT_API_BASE_URL;
    // When running on a physical device, 'localhost' points to the device itself.
    // If Expo provides a debuggerHost (e.g. 192.168.x.y:19000) use its host IP instead.
    try {
      const dbg = (Constants.manifest && (Constants.manifest as any).debuggerHost) || null;
      if (dbg && base.includes('localhost')) {
        const host = String(dbg).split(':')[0];
        base = base.replace('localhost', host);
      }
    } catch {}
    return normalizeBackendBaseUrl(base, DEFAULT_API_BASE_URL);
  } catch {
    return DEFAULT_API_BASE_URL;
  }
}

function getWsBaseUrl(): string {
  if (Platform.OS === 'web' && typeof __APP_WS_BASE_URL__ !== 'undefined') {
    return normalizeBackendBaseUrl(__APP_WS_BASE_URL__, DEFAULT_WS_BASE_URL);
  }
  try {
    const Constants = require('expo-constants').default;
    let base = Constants.expoConfig?.extra?.wsBaseUrl || DEFAULT_WS_BASE_URL;
    try {
      const dbg = (Constants.manifest && (Constants.manifest as any).debuggerHost) || null;
      if (dbg && base.includes('localhost')) {
        const host = String(dbg).split(':')[0];
        base = base.replace('localhost', host);
      }
    } catch {}
    return normalizeBackendBaseUrl(base, DEFAULT_WS_BASE_URL);
  } catch {
    return DEFAULT_WS_BASE_URL;
  }
}

const config = {
  API_BASE_URL: getApiBaseUrl(),
  WS_BASE_URL: getWsBaseUrl(),
  
  // Zimbabwe boundaries (approximate bounding box)
  ZW_BOUNDS: {
    latMin: -22.5,
    latMax: -15.3,
    lngMin: 25.2,
    lngMax: 33.1,
  },
  
  // Polling intervals (fallback when WS unavailable)
  CHAT_POLL_INTERVAL: 10000,    // 10 seconds
  DEAL_POLL_INTERVAL: 15000,    // 15 seconds
  
  // WS reconnect
  WS_RECONNECT_BASE_DELAY: 1000,
  WS_RECONNECT_MAX_DELAY: 30000,
  WS_RECONNECT_MAX_ATTEMPTS: 10,
  
  // Commission default
  DEFAULT_COMMISSION_RATE: 0.10, // 10%
  
  // Phone
  ZW_COUNTRY_CODE: '+263',
};

export default config;
